/**
 * The form builder: define a register's own fields.
 *
 * Administrators only, and the screen says so rather than simply failing. A coordinator who
 * reaches this route sees why they cannot use it — a 403 rendered as an empty page is how a
 * permission rule becomes a support ticket.
 *
 * The shape of the work is: add fields as drafts, arrange them, then publish the lot as one
 * governed change. Drafts are inert — invisible to every grid, refused in every payload — so
 * a half-finished configuration cannot leak into a register. Publishing raises an application
 * that an administrative coordinator countersigns, because the person who makes a change
 * should not be the person who confirms it.
 *
 * A published field is not edited here either — it is *proposed against*. Renaming one and
 * retiring one both leave a marker on the field and take effect at the next published
 * version, through the same application. The distinction matters on screen: the row shows
 * what has been proposed and what it currently still is, because until the chain agrees the
 * register genuinely still says the old thing.
 *
 * What can never be proposed is a key or a kind. One decides where values are stored, the
 * other decides what they mean; changing either would leave the values already recorded
 * orphaned or misread. Those are the changes that really do need a retirement and a
 * replacement, and the screen says so at the point someone tries.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import type { RegisterInfo } from '@/api/types'
import { navigate } from '@/app/routes'
import { useSession } from '@/app/session'
import { Icon } from '@/components/Icon'
import { Empty, Failed, Skeleton } from '@/components/states'
import { FieldEditor } from '@/features/builder/FieldEditor'
import { FieldHistory } from '@/features/builder/FieldHistory'
import { registerLabel } from '@/lib/registers'

export type BuilderField = {
  id: string
  register_slug: string
  key: string
  label_en: string
  label_ar: string | null
  kind: string
  required: boolean
  help_text: string | null
  config: Record<string, unknown> | null
  condition: Record<string, unknown> | null
  section: string | null
  display_order: number
  in_grid: boolean
  width: number | null
  workbook_column: string | null
  status: 'draft' | 'published' | 'retired'
  introduced_in_version: number | null
  retired_in_version: number | null
  backfill: string
  backfilled_at: string | null
  pending: Record<string, unknown> | null
  retire_requested: boolean
}

export type BuilderView = {
  register_slug: string
  register_label: string
  schema_version: number
  fields: BuilderField[]
  reserved_keys: string[]
  has_drafts: boolean
  has_changes: boolean
}

export function FormBuilder({ slug }: { slug: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isAdmin = useSession((state) => state.hasRole('pmo_admin', 'platform_admin'))
  const [editing, setEditing] = useState<BuilderField | 'new' | null>(null)
  const [raised, setRaised] = useState<string | null>(null)

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: ({ signal }) => api.get<RegisterInfo[]>('/registers', signal),
    staleTime: Infinity,
  })

  const builder = useQuery({
    queryKey: ['builder', slug],
    queryFn: ({ signal }) => api.get<BuilderView>(`/registers/${slug}/fields`, signal),
    enabled: isAdmin,
  })

  const publish = useMutation({
    mutationFn: async () => {
      const application = await api.post<{ id: string; number: string }>('/applications', {
        application_type: 'schema_change',
        title: `Publish ${slug} fields`,
        entity_table: slug,
        payload: {},
      })
      await api.post(`/applications/${application.id}/submit`)
      return application
    },
    onSuccess: (application) => {
      setRaised(application.number)
      void queryClient.invalidateQueries({ queryKey: ['builder', slug] })
      void queryClient.invalidateQueries({ queryKey: ['inbox'] })
    },
  })

  // Not an empty page. Someone who cannot use this screen should be told why, by whom it can
  // be used, and where to go instead.
  if (!isAdmin) {
    return (
      <main className="page" data-testid="builder-forbidden">
        <header className="head">
          <div>
            <p className="head__eyebrow">{t('nav.builder')}</p>
            <h1 className="head__title">{t('builder.title')}</h1>
          </div>
        </header>
        <section className="card card--pad">
          <Empty title={t('builder.adminOnly')} hint={t('builder.adminOnlyHint')} />
        </section>
      </main>
    )
  }

  if (builder.isError) {
    const error = builder.error
    return (
      <main className="page">
        <Failed
          message={error instanceof ApiError ? error.friendly : String(error)}
          onRetry={() => void builder.refetch()}
        />
      </main>
    )
  }

  const catalogue = registers.data ?? []
  const drafts = (builder.data?.fields ?? []).filter((field) => field.status === 'draft')
  const published = (builder.data?.fields ?? []).filter((field) => field.status === 'published')
  // Renames and retirements outstanding on published fields. Counted separately from drafts
  // because they publish from a different card and read as a different kind of change.
  const proposals = published.filter(
    (field) => field.retire_requested || (field.pending && Object.keys(field.pending).length > 0),
  ).length

  return (
    <main className="page" data-testid="builder">
      <header className="head">
        <div>
          <p className="head__eyebrow">{t('nav.builder')}</p>
          <h1 className="head__title">{t('builder.title')}</h1>
          <p className="head__count">
            {t('builder.version', { version: builder.data?.schema_version ?? 0 })}
          </p>
        </div>
      </header>

      {raised ? (
        <p className="banner banner--good" role="status" data-testid="builder-raised">
          <Icon name="check" size={15} strokeWidth={2.4} />
          <span className="banner__text">{t('builder.raised', { number: raised })}</span>
          <button
            type="button"
            className="banner__action"
            onClick={() => navigate({ name: 'inbox' })}
          >
            {t('edit.viewInbox')}
          </button>
        </p>
      ) : null}

      <nav className="tabs" aria-label={t('package.registers')} data-scroll="1">
        {catalogue.map((register) => (
          <button
            key={register.slug}
            type="button"
            className="tabs__tab"
            data-register={register.slug}
            aria-current={register.slug === slug ? 'page' : undefined}
            onClick={() => {
              setEditing(null)
              setRaised(null)
              navigate({ name: 'builder', slug: register.slug })
            }}
            data-testid={`builder-tab-${register.slug}`}
          >
            <span className="tabs__dot" aria-hidden="true" />
            {registerLabel(t, register.slug, register.label)}
          </button>
        ))}
      </nav>

      {builder.isPending ? (
        <Skeleton rows={5} />
      ) : (
        <>
          <section className="card card--pad" data-testid="builder-drafts">
            <header className="builder__head">
              <div>
                <h2 className="card__title">{t('builder.drafts')}</h2>
                <p className="card__caption">{t('builder.draftsNote')}</p>
              </div>
              <button
                type="button"
                className="button button--primary"
                onClick={() => setEditing('new')}
                data-testid="builder-add"
              >
                <Icon name="plus" size={14} strokeWidth={2.4} />
                {t('builder.add')}
              </button>
            </header>

            {drafts.length === 0 ? (
              <Empty title={t('builder.noDrafts')} hint={t('builder.noDraftsHint')} />
            ) : (
              <>
                <FieldTable
                  fields={drafts}
                  slug={slug}
                  onEdit={(field) => setEditing(field)}
                  mode="draft"
                />
                <div className="edit__actions">
                  <button
                    type="button"
                    className="button button--primary"
                    disabled={publish.isPending}
                    onClick={() => publish.mutate()}
                    data-testid="builder-publish"
                  >
                    {publish.isPending
                      ? t('builder.publishing')
                      : t('builder.publish', { count: drafts.length })}
                  </button>
                </div>
                {publish.isError ? (
                  <p className="modal__error" role="alert" data-testid="builder-publish-error">
                    {publish.error instanceof ApiError
                      ? publish.error.friendly
                      : String(publish.error)}
                  </p>
                ) : null}
                <p className="card__note">{t('builder.publishNote')}</p>
              </>
            )}
          </section>

          <section className="card card--pad" data-testid="builder-published">
            <h2 className="card__title">{t('builder.published')}</h2>
            <p className="card__caption card__caption--note">{t('builder.publishedNote')}</p>
            {published.length === 0 ? (
              <Empty title={t('builder.nonePublished')} />
            ) : (
              <>
                <FieldTable
                  fields={published}
                  slug={slug}
                  onEdit={(field) => setEditing(field)}
                  mode="published"
                />
                {/* The same publish control as the drafts card. A proposed rename with no
                    draft beside it would otherwise be stranded — visible, agreed, and with
                    nothing on the screen able to enact it. */}
                {proposals > 0 ? (
                  <>
                    <div className="edit__actions">
                      <button
                        type="button"
                        className="button button--primary"
                        disabled={publish.isPending}
                        onClick={() => publish.mutate()}
                        data-testid="builder-publish-changes"
                      >
                        {t('builder.publishChanges', { count: proposals })}
                      </button>
                    </div>
                    <p className="card__note">{t('builder.proposalNote')}</p>
                  </>
                ) : null}
              </>
            )}
          </section>

          {/* Under the fields it describes, on the screen where the changes are made. A
              trail filed somewhere else is one nobody consults until an audit forces it. */}
          <FieldHistory slug={slug} />
        </>
      )}

      {editing ? (
        <FieldEditor
          slug={slug}
          field={editing === 'new' ? null : editing}
          reserved={builder.data?.reserved_keys ?? []}
          taken={(builder.data?.fields ?? []).map((field) => field.key)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            setRaised(null)
            void queryClient.invalidateQueries({ queryKey: ['builder', slug] })
            void queryClient.invalidateQueries({ queryKey: ['audit', slug] })
          }}
        />
      ) : null}
    </main>
  )
}

function FieldTable({
  fields,
  slug,
  onEdit,
  mode,
}: {
  fields: BuilderField[]
  slug: string
  onEdit: (field: BuilderField) => void
  mode: 'draft' | 'published'
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // Both, always. The trail is rendered on this screen from the changes these mutations
  // make, so refreshing the fields without the history would leave the panel describing the
  // state before the click that is still under the reader's cursor.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['builder', slug] })
    void queryClient.invalidateQueries({ queryKey: ['audit', slug] })
  }

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/registers/${slug}/fields/${id}`),
    onSuccess: refresh,
  })

  const retire = useMutation({
    mutationFn: (id: string) => api.post(`/registers/${slug}/fields/${id}/retire`, {}),
    onSuccess: refresh,
  })

  const withdraw = useMutation({
    mutationFn: (id: string) => api.delete(`/registers/${slug}/fields/${id}/retire`),
    onSuccess: refresh,
  })

  return (
    <div className="grid__scroll" data-scroll="1">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">{t('builder.label')}</th>
            <th scope="col">{t('builder.key')}</th>
            <th scope="col">{t('builder.kind')}</th>
            <th scope="col">{t('builder.required')}</th>
            <th scope="col">{t('builder.inGrid')}</th>
            <th scope="col">{t('builder.workbook')}</th>
            <th scope="col">
              <span className="visually-hidden">{t('grid.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => {
            const proposed = (field.pending ?? {}) as Record<string, unknown>
            const renamed = typeof proposed['label_en'] === 'string'
            return (
              <tr
                key={field.id}
                data-testid="builder-row"
                data-key={field.key}
                data-proposed={
                  field.retire_requested || Object.keys(proposed).length > 0 ? 'true' : undefined
                }
              >
                <td>
                  {/* Both names, while a rename is only proposed. Showing the new one alone
                      would say the change has happened; showing the old one alone would hide
                      that anything is outstanding. */}
                  {renamed ? (
                    <span className="builder__rename" data-testid="builder-proposed-label">
                      <s className="builder__was">{field.label_en}</s>
                      <Icon name="chevronEnd" size={13} strokeWidth={2} aria-hidden="true" />
                      <strong>{String(proposed['label_en'])}</strong>
                    </span>
                  ) : (
                    field.label_en
                  )}
                </td>
                <td className="numeric table__muted">{field.key}</td>
                <td>{t(`builder.kinds.${field.kind}`, field.kind)}</td>
                <td>{field.required ? t('value.yes') : t('value.no')}</td>
                <td>{field.in_grid ? t('value.yes') : t('value.no')}</td>
                <td className="table__muted">{field.workbook_column ?? '—'}</td>
                <td className="table__numeric">
                  {mode === 'draft' ? (
                    <span className="builder__actions">
                      <button
                        type="button"
                        className="button button--quiet button--small"
                        onClick={() => onEdit(field)}
                        data-testid="builder-edit"
                      >
                        {t('builder.edit')}
                      </button>
                      <button
                        type="button"
                        className="button button--quiet button--small"
                        onClick={() => remove.mutate(field.id)}
                        data-testid="builder-delete"
                      >
                        {t('builder.discard')}
                      </button>
                    </span>
                  ) : field.retire_requested ? (
                    // Proposed for retirement. The only control left is undo — stacking a
                    // rename on top of a removal is a change nobody could describe.
                    <span className="builder__actions">
                      <span className="chip chip--watch" data-testid="builder-retiring">
                        {t('builder.retiring')}
                      </span>
                      <button
                        type="button"
                        className="button button--quiet button--small"
                        onClick={() => withdraw.mutate(field.id)}
                        data-testid="builder-withdraw"
                      >
                        {t('builder.withdraw')}
                      </button>
                    </span>
                  ) : (
                    <span className="builder__actions">
                      <span className="chip chip--info" data-testid="builder-version">
                        {t('builder.sinceVersion', {
                          version: field.introduced_in_version ?? 0,
                        })}
                      </span>
                      <button
                        type="button"
                        className="button button--quiet button--small"
                        onClick={() => onEdit(field)}
                        data-testid="builder-rename"
                      >
                        {t('builder.rename')}
                      </button>
                      <button
                        type="button"
                        className="button button--quiet button--small"
                        onClick={() => retire.mutate(field.id)}
                        data-testid="builder-retire"
                      >
                        {t('builder.retire')}
                      </button>
                      {Object.keys(proposed).length > 0 ? (
                        <button
                          type="button"
                          className="button button--quiet button--small"
                          onClick={() => withdraw.mutate(field.id)}
                          data-testid="builder-withdraw"
                        >
                          {t('builder.withdraw')}
                        </button>
                      ) : null}
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
